// SETTINGS V2 — Lokácie (Locations) page (SETTINGS_V2_SP4 §5/§6/§7). Routed real
// page. Binds the reused customerLocationsSlice UNCHANGED — error surfacing is
// per-dispatch result matching (.rejected/.fulfilled.match) + page-local state;
// success gated on fulfilled (fixes legacy's silent-error `.then()`-on-both bug);
// refetch + invalidateMachines stay BINDING on every SUCCESSFUL write. The Enable
// no-op is now FIXED (SP4.2 — wires POST /enable, BE2 §5); the lat/lng-wipe backend
// bug (BE1) is separate. Inline styles + scoped CSS-var tokens + v2 atoms + Radix. No
// MUI/sx/dt/Tailwind. Page ROOT carries NO animation (SP2 toast-stacking rule).
//
// SETTINGS-V2-LOC-B: deactivated locations are HIDDEN by default; a header eye
// toggle (LocationsInactiveToggle) reveals them (greyed rows, Aktivovať intact).
// HIERARCHY EDGE CASE: a disabled parent that still has ACTIVE children is kept
// visible (greyed) rather than hidden — hiding it would orphan the children;
// only its disabled children + disabled leaf-parents are dropped. Show-all
// returns the tree untouched (full parity with the pre-toggle behavior). Toggle
// state is component-local (no persistence).
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import i18n from "../../../../core/i18n";
import Loader from "../../../../shared/components/Loader";
import { translateApiError } from "../../../../shared/utils/translateApiError";
import type { FormSelectOption } from "../../../../shared/components/redesign/forms/FormSelect";
import {
  fetchLocations,
  createLocation,
  updateLocation,
  disableLocation,
  enableLocation,
  setDefaultLocation,
  deleteLocationPermanently,
} from "../../../machinery/customerLocationsSlice";
import type { LocationDeleteBlocked } from "../../../machinery/api/customerLocationsApi";
import { invalidateMachines } from "../../../machinery/customerMachinerySlice";
import type { Location, LocationFormData } from "../../../machinery/components/locations/locationTypes";
import { closeDialog, openDialog } from "../settingsV2UiSlice";
import { SErrorCallout } from "../atoms";
import LocationRowV2 from "./LocationRowV2";
import LocationsInactiveToggle from "./LocationsInactiveToggle";
import LocationsMapV2 from "./LocationsMapV2";
import LocationDialogV2 from "../dialogs/LocationDialogV2";
import SettingsV2ConfirmDialog from "../dialogs/SettingsV2ConfirmDialog";

// One page-local error union (§7). Row 5 (403) → forbidden (title-only, no retry).
// LOC-DELETE adds two callout kinds: `deleteBlocked` (409 LOC_DELETE_BLOCKED —
// composed steering body + optional Deaktivovať action) and `deleteUnverified`
// (409 LOC_DELETE_UNVERIFIED — retryable). Both render on the same page-level
// SErrorCallout atom.
type PageError =
  | { kind: "load" | "action"; message?: string; retry: () => void }
  | { kind: "forbidden" }
  | { kind: "deleteBlocked"; body: string; actionLabel?: string; actionTooltip?: string; onAction?: () => void }
  | { kind: "deleteUnverified"; retry: () => void };

// Total deactivated nodes across the tree (parents + children). Pure.
const countInactive = (locs: Location[]): number =>
  locs.reduce(
    (n, l) => n + (l.isDisabled ? 1 : 0) + countInactive(l.childLocations ?? []),
    0
  );

const LocationsPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");

  const locations = useAppSelector((s) => s.customerLocations.locations);
  const loading = useAppSelector((s) => s.customerLocations.loading);
  const openDialogId = useAppSelector((s) => s.settingsV2Ui.openDialog);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editLocation, setEditLocation] = useState<Location | null>(null);
  const [disableTarget, setDisableTarget] = useState<Location | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Location | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [pageError, setPageError] = useState<PageError | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false); // component-local only
  // MAP-A-FE2 (§5.6): map pin-click → flash the matching row. Component-local,
  // auto-clears after the animation. Timer ref cleaned up on unmount.
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  // §enhancement: the selected/focused location (red map pin). Set by a row-pin
  // click OR a map-pin click; cleared by a map-background click. mapFocusNonce is
  // bumped only on ROW-pin clicks → tells the map to expand + pan/zoom to the pin.
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const [mapFocusNonce, setMapFocusNonce] = useState(0);

  // Deactivated rows the DEFAULT view hides = every disabled node EXCEPT a
  // disabled parent we keep to avoid orphaning its active children (that parent
  // stays greyed + reachable, so it isn't "hidden"). This equals exactly what
  // the toggle reveals — it drives both the count hint and whether the toggle
  // shows at all.
  const hiddenInactiveCount = useMemo(() => {
    const keptDisabledParents = locations.filter(
      (p) => p.isDisabled && (p.childLocations ?? []).some((c) => !c.isDisabled)
    ).length;
    return countInactive(locations) - keptDisabledParents;
  }, [locations]);

  // Default: drop deactivated rows. A disabled parent with active children is
  // kept (greyed, Aktivovať intact) with ONLY its active children — never
  // orphan a visible child. Show-all returns the tree untouched (parity).
  const visibleLocations = useMemo(() => {
    if (showInactive) return locations;
    return locations
      .map((p) => ({ ...p, childLocations: (p.childLocations ?? []).filter((c) => !c.isDisabled) }))
      .filter((p) => !p.isDisabled || p.childLocations.length > 0);
  }, [locations, showInactive]);

  // Shared success tail — every fulfilled write clears the page error, refetches
  // locations AND invalidates machines (location-anchored). BINDING pair (§4.3).
  const onWriteSuccess = () => {
    setPageError(null);
    dispatch(fetchLocations());
    dispatch(invalidateMachines());
  };

  // Fetch on mount (SEC-REQ-2), rejected-captured (§7 rows 1-2). GET has no module
  // gate → any failure is a load error (never 403).
  async function runFetch() {
    const r = await dispatch(fetchLocations());
    if (fetchLocations.rejected.match(r)) {
      setPageError({
        kind: "load",
        message: translateApiError(r.error, t, t("settings:locations.genericError")),
        retry: runFetch,
      });
    } else {
      setPageError(null);
    }
  }

  useEffect(() => {
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-expand the FIRST location once on load (legacy parity).
  useEffect(() => {
    if (locations.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set([locations[0].id]));
    }
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // MAP-A-FE2 (§5.6): a child pin first expands its parent, then (next tick, so
  // the just-rendered child row exists) scrolls the matching row to center and
  // flashes it. flashId clears after the 1.8s pulse.
  useEffect(() => () => {
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
  }, []);

  const flashRow = (id: string) => {
    setFlashId(id);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashId(null), 1800);
  };

  // Map-pin click → select (red pin) + scroll/flash the matching row.
  const handlePinClick = (loc: Location) => {
    setSelectedPinId(loc.id);
    const isChild = !!loc.parentLocationId;
    if (isChild && loc.parentLocationId) {
      const parentId = loc.parentLocationId;
      setExpandedIds((prev) => new Set(prev).add(parentId));
    }
    flashRow(loc.id);
    const scroll = () =>
      document
        .getElementById(`sv2-loc-${loc.id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (isChild) window.setTimeout(scroll, 0);
    else scroll();
  };

  // Row pin-icon click → select + focus the map (expand + pan/zoom) on this pin.
  // Only invoked for located rows (the icon is inert otherwise). Scroll the map
  // section into view first (matters on phone, where it defaults collapsed/off-screen).
  const handleRowPinClick = (loc: Location) => {
    if (loc.latitude == null || loc.longitude == null) return;
    setSelectedPinId(loc.id);
    setMapFocusNonce((n) => n + 1);
    flashRow(loc.id);
    document
      .getElementById("sv2-locations-map")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  // Map background (not a pin) → deselect.
  const handleMapBackgroundClick = () => setSelectedPinId(null);

  const closeLocationDialog = () => {
    dispatch(closeDialog());
    setEditLocation(null);
    setDialogError(null);
  };

  // Add = openLocationDialog(null); Edit = openLocationDialog(location).
  const openLocationDialog = (location: Location | null) => {
    setEditLocation(location);
    dispatch(openDialog("location"));
  };

  // Create / Update (§6.1) — fulfilled-gated. 403 → page forbidden callout (dialog
  // closes first). Other domain error → in-dialog callout (dialog stays open, form
  // preserved; the Save button IS the retry).
  const handleSave = async (data: LocationFormData) => {
    setDialogError(null);
    setSubmitting(true);
    const r = editLocation
      ? await dispatch(updateLocation({ id: editLocation.id, data }))
      : await dispatch(createLocation(data));
    setSubmitting(false);
    if (createLocation.rejected.match(r) || updateLocation.rejected.match(r)) {
      if (r.error.code === "403") {
        closeLocationDialog();
        setPageError({ kind: "forbidden" });
      } else {
        setDialogError(translateApiError(r.error, t, t("settings:locations.genericError")));
      }
      return;
    }
    onWriteSuccess();
    closeLocationDialog();
  };

  // Row-menu actions (§6.2 set-default / §6.3 disable / enable). All are
  // AsyncThunk<void,string> → one fulfilled-gated runner; retry re-dispatches the
  // same action. Enable now hits POST /v1/locations/{id}/enable (BE2, live .cc);
  // its 400 domain errors (parent-disabled / duplicate-name) render in the action
  // callout WITH retry, 403 → forbidden no-retry — identical to disable/set-default.
  async function runRowThunk(location: Location, action: typeof disableLocation) {
    setBusyRowId(location.id);
    const r = await dispatch(action(location.id));
    setBusyRowId(null);
    if (action.fulfilled.match(r)) return onWriteSuccess();
    setPageError(
      r.error.code === "403"
        ? { kind: "forbidden" }
        : {
            kind: "action",
            message: translateApiError(r.error, t, t("settings:locations.genericError")),
            retry: () => runRowThunk(location, action),
          }
    );
  }

  // Kebab: "Deaktivovať" → confirm (§6.4); "Aktivovať" → direct re-enable
  // (BE2 §5 — POST /enable via runRowThunk; no confirm dialog, idempotent 204).
  const handleDisableMenu = (location: Location) => {
    if (location.isDisabled) {
      runRowThunk(location, enableLocation);
    } else {
      setDisableTarget(location);
      dispatch(openDialog("disableLocation"));
    }
  };

  const handleConfirmDisable = () => {
    const target = disableTarget;
    if (!target) return;
    dispatch(closeDialog());
    setDisableTarget(null);
    runRowThunk(target, disableLocation);
  };

  const handleCancelDisable = () => {
    dispatch(closeDialog());
    setDisableTarget(null);
  };

  // ── LOC-DELETE (§7.3 / §7.4) — permanent hard delete ────────────────────────
  // Kebab "Vymazať natrvalo" → open the danger confirm (server 409 detail
  // arrives AFTER confirm — the accepted steer-via-409 UX).
  const handleDeleteMenu = (location: Location) => {
    setDeleteTarget(location);
    dispatch(openDialog("deleteLocation"));
  };

  // Compose the §7.4 blocked/unverified callout from the FROZEN 409 body.
  // DEFENSIVE: the reasons are NEVER silently dropped. Unknown/renamed
  // `referencedBy` tokens render as the raw token (+ count); a counts-only payload
  // still lists reasons (reasons derived from counts keys); and a blocked-but-
  // unparseable payload still shows a meaningful body + the Deaktivovať escape
  // hatch — a bare "can't delete" title with no WHY is never shown.
  function buildBlockedError(blocked: LocationDeleteBlocked, target: Location): PageError {
    if (blocked?.code === "LOC_DELETE_UNVERIFIED") {
      // Part of the system couldn't be reached — safe to retry (server re-checks).
      return { kind: "deleteUnverified", retry: () => runDelete(target) };
    }
    const counts =
      blocked?.counts && typeof blocked.counts === "object" ? blocked.counts : {};
    // Prefer referencedBy; if it's absent/empty but counts carries keys, derive the
    // reason list from the counts keys (defensive against a partial payload).
    const referencedBy =
      Array.isArray(blocked?.referencedBy) && blocked.referencedBy.length
        ? blocked.referencedBy
        : Object.keys(counts);
    // "stroje (5), servisné požiadavky (12)" — `default` steers via its own append
    // line, so it is not a labelled fragment.
    const listReasons = referencedBy.filter((reason) => reason !== "default");
    const fragments = listReasons.map((reason) => {
      const key = `settings:locations.blockedType.${reason}`;
      // Unknown/renamed token → raw token, NEVER the i18n key string or nothing.
      const label = i18n.exists(key) ? t(key) : reason;
      const count = counts[reason];
      return count != null ? `${label} (${count})` : label;
    });
    // "Usage" = any reason that isn't children/default — the five known holders AND
    // any future/unknown token. Drives the migration hint + the Deaktivovať offer.
    const hasUsage = listReasons.some((reason) => reason !== "children");
    const parts: string[] = [];
    if (fragments.length) {
      parts.push(t("settings:locations.deleteBlockedIntro", { list: fragments.join(", ") }));
    }
    if (hasUsage) parts.push(t("settings:locations.deleteBlockedMigrationHint"));
    if (referencedBy.includes("default")) parts.push(t("settings:locations.deleteBlockedDefault"));
    if (referencedBy.includes("children")) parts.push(t("settings:locations.deleteBlockedChildren"));
    // Last resort: blocked but no parseable reason at all → still give a meaningful
    // body so the callout is never just a title.
    if (parts.length === 0) parts.push(t("settings:locations.deleteBlockedGeneric"));
    // Deaktivovať escape hatch — offered when real usage blocks (or reasons were
    // unparseable) AND the row can actually be disabled (default is server-blocked;
    // a disabled row needs no steering). Wires to the existing disable-confirm flow.
    const offerDisable =
      (hasUsage || fragments.length === 0) && !target.isDefault && !target.isDisabled;
    return {
      kind: "deleteBlocked",
      body: parts.join(" "),
      actionLabel: offerDisable ? t("settings:locations.disable") : undefined,
      // Same Deaktivovať explainer as the row-menu item (consistency).
      actionTooltip: offerDisable ? t("settings:locations.tooltipDisable") : undefined,
      onAction: offerDisable ? () => handleDisableMenu(target) : undefined,
    };
  }

  // Run the delete thunk (also the unverified/network retry target). 409 rides
  // through as a FULFILLED union payload (§7.1 transport); only 403/404/5xx reject.
  async function runDelete(target: Location) {
    setBusyRowId(target.id);
    const r = await dispatch(deleteLocationPermanently(target.id));
    setBusyRowId(null);
    if (deleteLocationPermanently.fulfilled.match(r)) {
      if (r.payload.deleted) {
        setDeleteTarget(null);
        return onWriteSuccess(); // clear error + refetch + invalidateMachines (BINDING)
      }
      return setPageError(buildBlockedError(r.payload.blocked, target));
    }
    setPageError(
      r.error.code === "403"
        ? { kind: "forbidden" } // no retry (SEC-REQ-6)
        : {
            kind: "action",
            message: translateApiError(r.error, t, t("settings:locations.genericError")),
            retry: () => runDelete(target),
          }
    );
  }

  const handleConfirmDelete = () => {
    const target = deleteTarget;
    if (!target) return;
    dispatch(closeDialog());
    runDelete(target);
  };

  const handleCancelDelete = () => {
    dispatch(closeDialog());
    setDeleteTarget(null);
  };

  // Parent-select options: top-level locations, excluding self in edit mode (§5.1).
  const parentOptions: FormSelectOption[] = locations
    .filter((loc) => !editLocation || loc.id !== editLocation.id)
    .map((loc) => ({ value: loc.id, label: loc.name }));

  // Page error callout content (§7). Maps every PageError kind to the shared
  // SErrorCallout props. forbidden → title-only, no action; load/action →
  // title+body+retry; deleteBlocked → composed body + optional Deaktivovať;
  // deleteUnverified → retry the delete.
  const pageErrorView = (() => {
    if (!pageError) return null;
    switch (pageError.kind) {
      case "load":
        return {
          title: t("settings:locations.loadFailed"),
          body: pageError.message || t("settings:locations.genericError"),
          actionLabel: t("settings:locations.retry"),
          actionTooltip: undefined as string | undefined,
          onAction: pageError.retry,
        };
      case "action":
        return {
          title: t("settings:locations.actionFailed"),
          body: pageError.message || t("settings:locations.genericError"),
          actionLabel: t("settings:locations.retry"),
          actionTooltip: undefined as string | undefined,
          onAction: pageError.retry,
        };
      case "forbidden":
        return {
          title: t("settings:locations.permissionDenied"),
          body: undefined as string | undefined,
          actionLabel: undefined as string | undefined,
          actionTooltip: undefined as string | undefined,
          onAction: undefined as (() => void) | undefined,
        };
      case "deleteBlocked":
        return {
          title: t("settings:locations.deleteBlockedTitle"),
          body: pageError.body,
          actionLabel: pageError.actionLabel,
          actionTooltip: pageError.actionTooltip,
          onAction: pageError.onAction,
        };
      case "deleteUnverified":
        return {
          title: t("settings:locations.deleteUnverifiedTitle"),
          body: t("settings:locations.deleteUnverifiedBody"),
          actionLabel: t("settings:locations.retry"),
          actionTooltip: undefined as string | undefined,
          onAction: pageError.retry,
        };
    }
  })();

  const renderList = () => {
    if (loading && locations.length === 0) {
      return <div style={{ display: "grid", placeItems: "center", padding: "48px 0" }}><Loader /></div>;
    }
    // Fetch-failed empty → the page callout above is the only content (§5.1).
    if (locations.length === 0) {
      if (pageError) return null;
      return (
        <div
          style={{
            border: "1px dashed var(--border)", borderRadius: 8, padding: "32px 20px",
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 6, textAlign: "center",
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{t("settings:locations.emptyTitle")}</div>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{t("settings:locations.emptyHint")}</div>
        </div>
      );
    }
    // Locations exist but every one is deactivated → default view is empty.
    // Show an empty-ish state with a prominent reveal, not a bare page. (Only
    // reachable when !showInactive, since show-all yields the non-empty tree.)
    if (visibleLocations.length === 0) {
      return (
        <div
          style={{
            border: "1px dashed var(--border)", borderRadius: 8, padding: "32px 20px",
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 8, textAlign: "center",
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{t("settings:locations.allInactiveTitle")}</div>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{t("settings:locations.allInactiveHint")}</div>
          <button
            type="button"
            className="btn"
            onClick={() => setShowInactive(true)}
            style={{ marginTop: 2 }}
          >
            {t("settings:locations.showInactive")}
          </button>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visibleLocations.map((loc, index) => (
          <div
            key={loc.id}
            style={{
              display: "flex", flexDirection: "column", gap: 8,
              animation: `sv2-card-reveal 0.4s ease-out ${index * 0.06}s both`,
            }}
          >
            <LocationRowV2
              location={loc}
              domId={`sv2-loc-${loc.id}`}
              flashing={flashId === loc.id}
              selected={selectedPinId === loc.id}
              onPinIconClick={handleRowPinClick}
              expanded={expandedIds.has(loc.id)}
              onToggleExpand={() => toggleExpand(loc.id)}
              onEdit={openLocationDialog}
              onSetDefault={(loc2) => runRowThunk(loc2, setDefaultLocation)}
              onDisable={handleDisableMenu}
              onDelete={handleDeleteMenu}
              busy={busyRowId === loc.id}
            />
            {expandedIds.has(loc.id) &&
              loc.childLocations?.map((child) => (
                <LocationRowV2
                  key={child.id}
                  location={child}
                  domId={`sv2-loc-${child.id}`}
                  flashing={flashId === child.id}
                  selected={selectedPinId === child.id}
                  onPinIconClick={handleRowPinClick}
                  isChild
                  onEdit={openLocationDialog}
                  onDisable={handleDisableMenu}
                  onDelete={handleDeleteMenu}
                  busy={busyRowId === child.id}
                />
              ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    // Page root: NO animation (SP2 §2 toast-stacking rule).
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0, flex: 1 }}>
      {/* Header row (designer PageLokacie). */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--muted)", flex: 1, minWidth: 0 }}>{t("settings:locations.subtitle")}</div>
        <LocationsInactiveToggle
          showInactive={showInactive}
          hiddenCount={hiddenInactiveCount}
          onToggle={setShowInactive}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => openLocationDialog(null)}
          style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          ＋ {t("settings:locations.addLocation")}
        </button>
      </div>

      {/* MAP-A-FE2: overview map section (render order header → map → callout →
          list, §5.4). Pins derive from the SAME visibleLocations the list draws.
          Anchor id lets a row-pin click scroll the section into view (phone). */}
      <div id="sv2-locations-map">
        <LocationsMapV2
          locations={visibleLocations}
          onPinClick={handlePinClick}
          selectedId={selectedPinId}
          focusNonce={mapFocusNonce}
          onBackgroundClick={handleMapBackgroundClick}
        />
      </div>

      {/* Page-level error callout (§7 rows 1-2, 4-5 + LOC-DELETE blocked/unverified). */}
      {pageErrorView && (
        <SErrorCallout
          title={pageErrorView.title}
          body={pageErrorView.body}
          actionLabel={pageErrorView.actionLabel}
          actionTooltip={pageErrorView.actionTooltip}
          onAction={pageErrorView.onAction}
        />
      )}

      {renderList()}

      {/* Mono footnote — cascade + max-2-levels + default-can't-disable (§5.1). */}
      {locations.length > 0 && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", letterSpacing: "0.04em" }}>{t("settings:locations.footnote")}</div>
      )}

      {/* Create/edit dialog (reads its own open-state from Redux). */}
      <LocationDialogV2
        editLocation={editLocation}
        parentOptions={parentOptions}
        onSave={handleSave}
        onClose={closeLocationDialog}
        submitting={submitting}
        dialogError={dialogError}
      />

      {/* Disable confirm (§6.4 — cascade text only for parents with children). */}
      <SettingsV2ConfirmDialog
        open={openDialogId === "disableLocation"}
        onClose={handleCancelDisable}
        title={t("settings:locations.disableConfirmTitle")}
        text={
          disableTarget?.childLocations?.length
            ? t("settings:locations.disableConfirmTextCascade", { name: disableTarget?.name ?? "" })
            : t("settings:locations.disableConfirmText", { name: disableTarget?.name ?? "" })
        }
        confirmLabel={t("settings:locations.disable")}
        cancelLabel={tCommon("buttons.cancel")}
        danger
        onConfirm={handleConfirmDisable}
      />

      {/* Permanent-delete confirm (LOC-DELETE §7.3) — danger. The blocked/
          unverified 409 detail surfaces on the page callout AFTER confirm. */}
      <SettingsV2ConfirmDialog
        open={openDialogId === "deleteLocation"}
        onClose={handleCancelDelete}
        title={t("settings:locations.deleteConfirmTitle")}
        text={t("settings:locations.deleteConfirmText", { name: deleteTarget?.name ?? "" })}
        confirmLabel={t("settings:locations.deletePermanently")}
        cancelLabel={tCommon("buttons.cancel")}
        danger
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default LocationsPageV2;
